import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const AUTH_URL = 'https://dev-auth.apps.istor.io';
const CLOUDINARY_CLOUD_NAME = 'istorio';
const CLOUDINARY_UPLOAD_PRESET = 'maker_default';

const getCookie = (name) => {
    const m = document.cookie.split('; ').find(r => r.startsWith(`${name}=`));
    return m ? m.split('=').slice(1).join('=') : undefined;
};

// Largeur du viewport, mise à jour au resize (pour reflow de la modal de crop).
const useViewportWidth = () => {
    const [w, setW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
    useEffect(() => {
        const onResize = () => setW(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return w;
};

const uploadToCloudinary = async (blob) => {
    const fd = new FormData();
    fd.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    fd.append('file', blob, 'image');
    const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/upload`, {
        method: 'POST',
        body: fd,
    });
    if (!res.ok) throw new Error('Échec du téléversement Cloudinary');
    return res.json();
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// Formats que le sélecteur laisse passer (accept="image/*") mais que Chrome et
// Firefox ne décodent pas, contrairement à Safari.
const isUnsupportedByBrowser = (file) =>
    /^image\/hei[cf]/i.test(file?.type || '') || /\.hei[cf]$/i.test(file?.name || '');

// frameW/frameH : taille d'édition à l'écran. outW/outH : résolution de l'image exportée.
// overlayTop : haut du voile (61 = sous la navbar d'InstancePage, 0 = plein écran).
const SHAPES = {
    logo: { frameW: 260, frameH: 260, radius: '50%', outW: 480, outH: 480, mime: 'image/png', quality: 1, title: 'Modifier le logo', overlayTop: 61 },
    banner: { frameW: 560, frameH: (560 * 460) / 1240, radius: '20px', outW: 1240, outH: 460, mime: 'image/jpeg', quality: 0.92, title: 'Modifier la bannière', overlayTop: 61 },
    avatar: { frameW: 260, frameH: 260, radius: '50%', outW: 480, outH: 480, mime: 'image/png', quality: 1, title: 'Modifier la photo de profil', overlayTop: 0 },
};

function ImageCropModal({ file, kind, onCancel, onConfirm, uploading, error }) {
    const cfg = SHAPES[kind];
    // Cadre d'édition responsive : rétréci pour tenir sur écran étroit
    // (20px de marge de chaque côté + 30px de padding interne de chaque côté),
    // tout en gardant le ratio d'origine. La résolution exportée (outW/outH) reste fixe.
    const viewportW = useViewportWidth();
    const availW = viewportW - 100;
    const frameW = Math.max(180, Math.min(cfg.frameW, availW));
    const frameH = cfg.frameH * (frameW / cfg.frameW);
    const [imgEl, setImgEl] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const pointersRef = useRef(new Map()); // pointerId -> {x, y} (multi-touch)
    const gestureRef = useRef(null);       // { mode: 'drag'|'pinch', ... }
    const [hoveredCancel, setHoveredCancel] = useState(false);
    const [activeCancel, setActiveCancel] = useState(false);
    const [hoveredConfirm, setHoveredConfirm] = useState(false);
    const [activeConfirm, setActiveConfirm] = useState(false);

    useEffect(() => {
        if (!file) { setImgEl(null); setLoadError(null); return; }
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            setLoadError(null);
            setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
            setImgEl(img);
            setZoom(1);
            setOffset({ x: 0, y: 0 });
        };
        // Le navigateur ne sait pas décoder tous les formats que le sélecteur de
        // fichiers laisse passer avec accept="image/*" : Chrome refuse HEIC/HEIF
        // (photos iPhone) et les JPEG en CMJN, là où Safari les ouvre. Sans ce
        // onerror l'image restait nulle en silence et « Valider » ne faisait rien.
        img.onerror = () => {
            setImgEl(null);
            setNaturalSize({ w: 0, h: 0 });
            setLoadError(isUnsupportedByBrowser(file)
                ? "Ce format d'image (HEIC/HEIF) n'est pas lisible par votre navigateur. Convertissez la photo en JPEG ou PNG."
                : "Cette image n'a pas pu être ouverte par votre navigateur. Essayez un fichier JPEG ou PNG.");
        };
        img.src = url;
        return () => URL.revokeObjectURL(url);
    }, [file]);

    if (!file) return null;

    const baseScale = naturalSize.w > 0
        ? Math.max(frameW / naturalSize.w, frameH / naturalSize.h)
        : 1;
    const scale = baseScale * zoom;
    const dispW = naturalSize.w * scale;
    const dispH = naturalSize.h * scale;

    const clampToFrame = (o, dW, dH) => {
        const maxX = Math.max(0, (dW - frameW) / 2);
        const maxY = Math.max(0, (dH - frameH) / 2);
        return { x: clamp(o.x, -maxX, maxX), y: clamp(o.y, -maxY, maxY) };
    };

    const pointerDist = (pts) => Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);

    const handlePointerDown = (e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const pts = [...pointersRef.current.values()];
        if (pts.length === 1) {
            // Un doigt (ou souris) : déplacement de la sélection.
            gestureRef.current = { mode: 'drag', startX: e.clientX, startY: e.clientY, origin: offset };
        } else if (pts.length === 2) {
            // Deux doigts : pincer pour (dé)zoomer.
            gestureRef.current = { mode: 'pinch', startDist: pointerDist(pts), startZoom: zoom };
        }
    };

    const handlePointerMove = (e) => {
        if (!pointersRef.current.has(e.pointerId)) return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const g = gestureRef.current;
        if (!g) return;
        const pts = [...pointersRef.current.values()];
        if (g.mode === 'drag' && pts.length === 1) {
            const dx = e.clientX - g.startX;
            const dy = e.clientY - g.startY;
            setOffset(clampToFrame({ x: g.origin.x + dx, y: g.origin.y + dy }, dispW, dispH));
        } else if (g.mode === 'pinch' && pts.length >= 2) {
            const ratio = pointerDist(pts) / (g.startDist || 1);
            const newZoom = clamp(g.startZoom * ratio, 1, 3);
            const newScale = baseScale * newZoom;
            const nDispW = naturalSize.w * newScale;
            const nDispH = naturalSize.h * newScale;
            setZoom(newZoom);
            setOffset((o) => clampToFrame(o, nDispW, nDispH));
        }
    };

    const handlePointerUp = (e) => {
        pointersRef.current.delete(e.pointerId);
        const pts = [...pointersRef.current.entries()];
        if (pts.length === 1) {
            // Il reste un doigt : on repasse en déplacement à partir de sa position.
            const [, p] = pts[0];
            gestureRef.current = { mode: 'drag', startX: p.x, startY: p.y, origin: offset };
        } else if (pts.length === 0) {
            gestureRef.current = null;
        }
    };

    const handleZoomChange = (e) => {
        const newZoom = parseFloat(e.target.value);
        const newScale = baseScale * newZoom;
        const nDispW = naturalSize.w * newScale;
        const nDispH = naturalSize.h * newScale;
        setZoom(newZoom);
        setOffset((o) => clampToFrame(o, nDispW, nDispH));
    };

    const handleValidate = () => {
        if (!imgEl) return;
        const k = cfg.outW / frameW;
        const canvas = document.createElement('canvas');
        canvas.width = cfg.outW;
        canvas.height = cfg.outH;
        const ctx = canvas.getContext('2d');
        const outDispW = dispW * k;
        const outDispH = dispH * k;
        const dx = (cfg.outW / 2 + offset.x * k) - outDispW / 2;
        const dy = (cfg.outH / 2 + offset.y * k) - outDispH / 2;
        ctx.drawImage(imgEl, dx, dy, outDispW, outDispH);
        canvas.toBlob((blob) => onConfirm(blob), cfg.mime, cfg.quality);
    };

    // En responsive, la modal devient un bottom sheet (cf. LeftMenu superadmin).
    const isSheet = viewportW <= 750;

    const modalStyle = isSheet ? {
        position: 'fixed', left: 0, right: 0, bottom: 0, top: 'auto',
        width: '100%', maxHeight: '75dvh', overflowY: 'auto',
        borderRadius: '20px 20px 0 0', backgroundColor: '#FFFFFF', zIndex: 2001,
        boxShadow: '0px -10px 40px rgba(0, 0, 0, 0.15)',
        display: 'flex', flexDirection: 'column',
        animation: 'crop-sheet-up 0.3s ease-in-out',
    } : {
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: frameW + 60,
        borderRadius: '20px', backgroundColor: '#FFFFFF', zIndex: 2001,
        boxShadow: '0px 10px 40px rgba(0, 0, 0, 0.2)',
        display: 'flex', flexDirection: 'column',
    };

    return createPortal(
        <>
            <div
                onClick={onCancel}
                style={{
                    position: 'fixed', top: isSheet ? 0 : `${cfg.overlayTop}px`, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(64,91,106,0.8)',
                    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                    zIndex: 2000,
                }}
            />
            <div style={modalStyle}>
                <div style={{
                    width: '100%', height: '60px', flexShrink: 0, backgroundColor: '#173C50',
                    borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', paddingLeft: '30px',
                }}>
                    <span style={{ fontWeight: 600, fontSize: '20px', color: '#FFFFFF', fontFamily: 'Lexend, system-ui, sans-serif' }}>{cfg.title}</span>
                </div>
                <div style={{ padding: '20px 30px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                        style={{
                            width: frameW, height: frameH, borderRadius: cfg.radius,
                            overflow: 'hidden', position: 'relative', backgroundColor: '#F4F4F7',
                            cursor: 'grab', touchAction: 'none',
                        }}
                    >
                        {imgEl && (
                            <img
                                src={imgEl.src}
                                alt=""
                                draggable={false}
                                style={{
                                    position: 'absolute',
                                    left: frameW / 2 + offset.x - dispW / 2,
                                    top: frameH / 2 + offset.y - dispH / 2,
                                    width: dispW,
                                    height: dispH,
                                    maxWidth: 'none',
                                    userSelect: 'none',
                                }}
                            />
                        )}
                    </div>
                    <input
                        type="range"
                        min={1}
                        max={3}
                        step={0.01}
                        value={zoom}
                        onChange={handleZoomChange}
                        style={{ width: frameW, marginTop: '16px' }}
                    />
                    {(loadError || error) && (
                        <div
                            role="alert"
                            style={{
                                width: '100%', marginTop: '16px', padding: '10px 12px', borderRadius: '10px',
                                backgroundColor: 'rgba(244,105,105,0.12)', border: '1px solid #F46969',
                                color: '#B42929', fontSize: '13px', fontWeight: 500, lineHeight: 1.4,
                                fontFamily: 'Lexend, system-ui, sans-serif',
                            }}
                        >
                            {loadError || error}
                        </div>
                    )}
                    <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'row', gap: '10px', justifyContent: 'flex-end', width: '100%' }}>
                        <button
                            onClick={onCancel}
                            onMouseEnter={() => setHoveredCancel(true)}
                            onMouseLeave={() => { setHoveredCancel(false); setActiveCancel(false); }}
                            onMouseDown={() => setActiveCancel(true)}
                            onMouseUp={() => setActiveCancel(false)}
                            style={{
                                height: '36px', borderRadius: '10px', padding: '0 18px', fontWeight: 600, fontSize: '13px',
                                fontFamily: 'Lexend, system-ui, sans-serif', color: '#F46969',
                                backgroundColor: activeCancel ? 'rgba(255,113,113,0.3)' : hoveredCancel ? 'rgba(255,202,202,0.3)' : 'transparent',
                                border: '1px solid #F46969', cursor: 'pointer', transition: 'background-color 0.15s ease',
                            }}
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleValidate}
                            disabled={uploading || !imgEl}
                            onMouseEnter={() => setHoveredConfirm(true)}
                            onMouseLeave={() => { setHoveredConfirm(false); setActiveConfirm(false); }}
                            onMouseDown={() => setActiveConfirm(true)}
                            onMouseUp={() => setActiveConfirm(false)}
                            style={{
                                height: '36px', borderRadius: '10px', padding: '0 18px', fontWeight: 600, fontSize: '13px',
                                fontFamily: 'Lexend, system-ui, sans-serif', color: '#FFFFFF',
                                backgroundColor: activeConfirm ? '#173C50' : hoveredConfirm ? '#006DBB' : '#007BD3',
                                border: `1px solid ${activeConfirm ? '#173C50' : hoveredConfirm ? '#006DBB' : '#007BD3'}`,
                                cursor: (uploading || !imgEl) ? 'default' : 'pointer', opacity: (uploading || !imgEl) ? 0.5 : 1,
                                transition: 'background-color 0.15s ease, border-color 0.15s ease',
                            }}
                        >
                            {uploading ? 'Envoi…' : 'Valider'}
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}

// Câble l'import + le recadrage + l'upload Cloudinary + la mise à jour de l'établissement
// (endpoints PATCH /organisation/:id/logo et /banner).
export function OrganizationImageUpload({ orgId, kind, onUploaded, children }) {
    const inputRef = useRef(null);
    const [pendingFile, setPendingFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    const trigger = () => inputRef.current?.click();

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) { setError(null); setPendingFile(file); }
    };

    const handleCancel = () => { setPendingFile(null); setError(null); };

    const handleConfirm = async (blob) => {
        if (!orgId) { setError('Établissement introuvable, rechargez la page.'); return; }
        if (!blob) { setError("Le recadrage n'a pas pu être généré."); return; }
        const jwt = getCookie('jwt');
        if (!jwt) { setError('Votre session a expiré, reconnectez-vous.'); return; }
        setUploading(true);
        setError(null);
        try {
            const uploaded = await uploadToCloudinary(blob);
            const res = await fetch(`${AUTH_URL}/organisation/${orgId}/${kind}`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: uploaded.secure_url, publicId: uploaded.public_id }),
            });
            if (!res.ok) throw new Error(`Enregistrement refusé par le serveur (${res.status}).`);
            const updatedOrganization = await res.json();
            onUploaded(updatedOrganization);
            setPendingFile(null);
        } catch (e) {
            // La modal reste ouverte avec le message : sinon l'échec est invisible.
            setError(e?.message || "L'enregistrement a échoué, réessayez.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <input type="file" accept="image/*" ref={inputRef} onChange={handleFileChange} style={{ display: 'none' }} />
            {children({ trigger, uploading })}
            <ImageCropModal
                file={pendingFile}
                kind={kind}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                uploading={uploading}
                error={error}
            />
        </>
    );
}

// Même mécanique que OrganizationImageUpload, pour la photo de profil de
// l'utilisateur connecté (endpoint PATCH /user/avatar, identifié par le JWT).
export function UserAvatarUpload({ onUploaded, children }) {
    const inputRef = useRef(null);
    const [pendingFile, setPendingFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    const trigger = () => inputRef.current?.click();

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) { setError(null); setPendingFile(file); }
    };

    const handleCancel = () => { setPendingFile(null); setError(null); };

    const handleConfirm = async (blob) => {
        if (!blob) { setError("Le recadrage n'a pas pu être généré."); return; }
        const jwt = getCookie('jwt');
        if (!jwt) { setError('Votre session a expiré, reconnectez-vous.'); return; }
        setUploading(true);
        setError(null);
        try {
            const uploaded = await uploadToCloudinary(blob);
            const res = await fetch(`${AUTH_URL}/user/avatar`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: uploaded.secure_url, publicId: uploaded.public_id }),
            });
            if (!res.ok) throw new Error(`Enregistrement refusé par le serveur (${res.status}).`);
            const updatedUser = await res.json();
            onUploaded(updatedUser);
            setPendingFile(null);
        } catch (e) {
            setError(e?.message || "L'enregistrement a échoué, réessayez.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <input type="file" accept="image/*" ref={inputRef} onChange={handleFileChange} style={{ display: 'none' }} />
            {children({ trigger, uploading })}
            <ImageCropModal
                file={pendingFile}
                kind="avatar"
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                uploading={uploading}
                error={error}
            />
        </>
    );
}
